import React from "react";
import { IUniform } from "../../interfaces/IUniform";
import UniformCard from "./UniformCard.tsx";
import UniformForm from "./UniformForm.tsx";
import ModalContainer from "../ModalContainer.tsx";

interface UniformsPanelProps {
  positionId: number;
  uniforms: IUniform[];
  onUniformsChange: (uniforms: IUniform[]) => void;
}

const UniformsPanel: React.FC<UniformsPanelProps> = ({
  positionId,
  uniforms,
  onUniformsChange,
}) => {
  const [showModal, setShowModal] = React.useState(false);
  const [editingUniform, setEditingUniform] = React.useState<IUniform | null>(null);

  // Helper function to format price
  const formatPrice = (price: number | string): string => {
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    return numPrice.toFixed(2);
  };

  const handleAddUniform = () => {
    setEditingUniform({
      name: "",
      description: "",
      unitPrice: 0,
      positionId: positionId,
    });
    setShowModal(true);
  };

  const handleEditUniform = (uniform: IUniform) => {
    setEditingUniform({
      ...uniform,
      unitPrice: typeof uniform.unitPrice === 'string' ? parseFloat(uniform.unitPrice) : uniform.unitPrice
    });
    setShowModal(true);
  };

  const handleSaveUniform = (uniformData: IUniform) => {
    if (editingUniform?.id && editingUniform.id < 1000000) {
      // Actualizar uniforme existente (IDs temporales son menores a 1000000)
      const updatedUniforms = uniforms.map(u => 
        u.id === editingUniform.id ? { ...uniformData, id: editingUniform.id } : u
      );
      onUniformsChange(updatedUniforms);
    } else {
      // Agregar nuevo uniforme
      const newUniform = {
        ...uniformData,
        id: Math.floor(Math.random() * 999999) + 1, // ID temporal pequeño para el frontend
      };
      onUniformsChange([...uniforms, newUniform]);
    }
    setShowModal(false);
    setEditingUniform(null);
  };

  const handleDeleteUniform = (index: number) => {
    if (window.confirm("¿Estás seguro de que deseas eliminar este uniforme?")) {
      const newUniforms = [...uniforms];
      newUniforms.splice(index, 1);
      onUniformsChange(newUniforms);
    }
  };

  const handleCancelForm = () => {
    setShowModal(false);
    setEditingUniform(null);
  };

  return (
    <React.Fragment>
      <div style={{ display: "flex", flexDirection: "row", marginTop: "1%" }}>
        <div className="col-md-1" />
        <div className="col-md-6">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => handleAddUniform()}
          >
            <i className="fas fa-plus"></i> Agregar uniforme
          </button>
        </div>
      </div>

      {uniforms.length > 0 ? (
        <div style={{ display: "flex", flexDirection: "row", marginTop: "1%" }}>
          <div className="col-md-1" />
          <div className="col-md-11">
            <div className="row">
              {uniforms.map((uniform, index) => (
                <div key={uniform.id || index} className="col-md-6 col-lg-4 mb-3">
                  <div className="card border h-100" 
                       style={{ 
                         borderRadius: '6px',
                         transition: 'box-shadow 0.2s ease'
                       }}
                       onMouseEnter={(e) => {
                         e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                       }}
                       onMouseLeave={(e) => {
                         e.currentTarget.style.boxShadow = 'none';
                       }}>
                    
                    {/* Header minimalista */}
                    <div className="card-header bg-light border-bottom p-2">
                      <div className="d-flex justify-content-between align-items-center">
                        <h6 className="card-title mb-0 fw-semibold text-dark">
                          {uniform.name}
                        </h6>
                        <div className="d-flex gap-1">
                          <button 
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => handleEditUniform(uniform)}
                            title="Editar uniforme"
                            style={{ 
                              width: '24px',
                              height: '24px',
                              padding: '0',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                            <i className="fa fa-edit" style={{ fontSize: '0.7rem' }}></i>
                          </button>
                          <button 
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDeleteUniform(index)}
                            title="Eliminar uniforme"
                            style={{ 
                              width: '24px',
                              height: '24px',
                              padding: '0',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                            <i className="fa fa-trash" style={{ fontSize: '0.7rem' }}></i>
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Body compacto */}
                    <div className="card-body p-3">
                      {/* Precio simple */}
                      <div className="mb-2">
                        <span className="badge bg-success text-white">
                          ${formatPrice(uniform.unitPrice)}
                        </span>
                      </div>

                      {/* Descripción compacta */}
                      <p className="card-text text-muted mb-0 small" 
                         style={{ 
                           fontSize: '0.85rem',
                           lineHeight: '1.4',
                           display: '-webkit-box',
                           WebkitLineClamp: 2,
                           WebkitBoxOrient: 'vertical',
                           overflow: 'hidden'
                         }}>
                        {uniform.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "row", marginTop: "1%" }}>
          <div className="col-md-1" />
          <div className="col-md-11">
            <div className="text-center py-5">
              <div className="mb-3">
                <i className="fas fa-tshirt text-muted" style={{ fontSize: '4rem' }}></i>
              </div>
              <p className="text-muted mb-0">No hay uniformes registrados para este puesto</p>
              <small className="text-muted">Haz clic en "Agregar uniforme" para comenzar</small>
            </div>
          </div>
        </div>
      )}

      <ModalContainer
        open={showModal}
        closeFn={() => setShowModal(false)}
        title={editingUniform?.id ? "Editar uniforme" : "Agregar uniforme"}
        children={
          <UniformForm
            uniform={editingUniform!}
            onSave={handleSaveUniform}
            onCancel={handleCancelForm}
            isEditing={!!editingUniform?.id}
          />
        }
      />
    </React.Fragment>
  );
};

export default UniformsPanel;
